import { Controller } from "@hotwired/stimulus"

const MAX_RETRIES = 3

export default class extends Controller {
  static values = { siteKey: { type: String, default: "" } }
  static targets = ["button", "container", "input"]

  connect() {
    if (this.siteKeyValue === "") {
      return
    }

    this.pendingSubmit = false
    this.retryCount = 0
    this.widgetId = null

    // Clear stale token that Turbo cache may have preserved
    this.inputTarget.value = ""

    if (!window.turnstile) {
      this.loadTurnstileScript()
    }

    this.boundSubmitHandler = (event) => {
      if (this.inputTarget.value === "") {
        event.preventDefault()
        this.initTurnstile()
      }
    }
    this.element.addEventListener("submit", this.boundSubmitHandler)

    this.boundTurnstileLoadHandler = () => {
      if (this.pendingSubmit) {
        this.pendingSubmit = false
        this.initTurnstile()
      }
    }
    document.addEventListener("turnstile:loaded", this.boundTurnstileLoadHandler)
  }

  loadTurnstileScript() {
    if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
      return
    }

    const script = document.createElement("script")
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad"
    script.defer = "defer"
    script.async = true

    window.onTurnstileLoad = () => {
      document.dispatchEvent(new CustomEvent("turnstile:loaded"))
    }
    document.body.appendChild(script)
  }

  disconnect() {
    this.clearTimeoutTimer()
    this.destroyWidget()

    if (this.boundSubmitHandler) {
      this.element.removeEventListener("submit", this.boundSubmitHandler)
      this.boundSubmitHandler = null
    }

    if (this.boundTurnstileLoadHandler) {
      document.removeEventListener("turnstile:loaded", this.boundTurnstileLoadHandler)
      this.boundTurnstileLoadHandler = null
    }
  }

  disableButton() {
    this.buttonTarget.disabled = true
    this.buttonTarget.classList.add("cursor-not-allowed", "opacity-50")
  }

  enableButton() {
    this.buttonTarget.disabled = false
    this.buttonTarget.classList.remove("cursor-not-allowed", "opacity-50")
  }

  initTurnstile() {
    if (!window.turnstile) {
      this.pendingSubmit = true
      this.disableButton()
      this.scheduleTimeout()
      return
    }

    this.disableButton()
    this.clearTimeoutTimer()

    // Ensure container is visible (Turbo cache may restore it with class="hidden")
    this.containerTarget.classList.remove("hidden")

    if (this.widgetId != null) {
      try {
        turnstile.reset(this.widgetId)
        return
      } catch {
        this.destroyWidget()
      }
    }

    this.renderWidget()
  }

  renderWidget() {
    this.widgetId = turnstile.render(this.containerTarget, {
      sitekey: this.siteKeyValue,
      theme: "light",
      size: "flexible",
      "error-callback": (errorCode) => {
        console.error("Turnstile error:", errorCode)
        this.handleTurnstileError(errorCode)
      },
      "expired-callback": () => {
        this.inputTarget.value = ""
        if (this.widgetId != null) {
          try {
            turnstile.reset(this.widgetId)
          } catch {
            this.destroyWidget()
          }
        }
      },
      "timeout-callback": () => {
        this.handleTurnstileError()
      },
      "unsupported-callback": () => {
        this.enableButton()
        App.displayFlash("warning", "Verification unavailable. You may proceed.")
      },
      "before-interactive-callback": () => {
        setTimeout(() => {
          App.displayFlash("warning", `Proceed with the verification to confirm you are human.`)
        }, 1000)
        this.disableButton()
        this.containerTarget.classList.remove("hidden")
      },
      "after-interactive-callback": () => {
        this.containerTarget.classList.add("hidden")
        this.enableButton()
      },
      callback: (token) => {
        this.clearTimeoutTimer()
        this.retryCount = 0
        this.inputTarget.value = token
        if (this.element.requestSubmit) {
          this.element.requestSubmit(this.buttonTarget)
        } else {
          this.element.submit()
        }
      },
    })
  }

  destroyWidget() {
    if (window.turnstile && this.widgetId != null) {
      try {
        turnstile.remove(this.widgetId)
      } catch {
        // Widget was already invalid
      }
    }
    this.widgetId = null
    this.containerTarget.innerHTML = ""
  }

  scheduleTimeout() {
    this.clearTimeoutTimer()
    this.timeoutId = setTimeout(() => {
      this.handleTurnstileError()
    }, 15000)
  }

  clearTimeoutTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }

  handleTurnstileError(errorCode = null) {
    this.enableButton()
    this.clearTimeoutTimer()
    this.pendingSubmit = false

    this.destroyWidget()
    this.retryCount++

    if (this.retryCount < MAX_RETRIES) {
      console.warn(`Turnstile error (${errorCode}), retrying (${this.retryCount}/${MAX_RETRIES})`)
      setTimeout(() => this.initTurnstile(), 1000)
      return
    }

    // Reset retry budget so the user's next manual attempt gets fresh retries
    this.retryCount = 0

    const message = errorCode
      ? `Turnstile verification failed (${errorCode}). Please try again.`
      : "Turnstile verification failed. Please try again."
    App.displayFlash("error", message)
  }
}
